import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Toast } from '../ui';
import Header from './Header';
import NoteContent from './NoteContent';
import PasswordInput from './PasswordInput';
import ExpirationSelector from './ExpirationSelector';
import ViewLimitInput from './ViewLimitInput';
import SecurityFeatures from './SecurityFeatures';
import ProtectionToggle from './ProtectionToggle';
import SubmitButton from './SubmitButton';
import useNoteCreation from '../../hooks/useNoteCreation';
import { MAX_NOTE_LENGTH } from '../../utils/validation';

export default function CreateNote() {
  const {
    content,
    setContent,
    password,
    setPassword,
    expiresInDays,
    setExpiresInDays,
    viewLimit,
    setViewLimit,
    isProtected,
    isSubmitting,
    passwordStrength,
    isCheckingPassword,
    isPasswordExposed,
    toast,
    setToast,
    handleSubmit,
    handleProtectionToggle,
    isSubmitDisabled,
  } = useNoteCreation();

  const navigate = useNavigate();
  const { t } = useTranslation('createNote');

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = await handleSubmit(e);
    if (result?.short_id) {
      navigate(`/share/${result.short_id}`, { 
        state: { 
          password: isProtected ? password : undefined 
        }
      });
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-6">
      <Header />
      
      <form onSubmit={handleFormSubmit} className="space-y-8">
        <div className="bg-white rounded-2xl shadow-sm border p-6 space-y-6">
          <NoteContent
            content={content}
            maxLength={MAX_NOTE_LENGTH}
            onChange={setContent}
          />

          <div className="flex flex-col md:flex-row gap-6">
            <div className="flex-1 space-y-4">
              <div className="flex items-center justify-between">
                <ProtectionToggle
                  isProtected={isProtected}
                  onToggle={handleProtectionToggle}
                />
              </div>

              {isProtected && (
                <PasswordInput
                  password={password}
                  isProtected={isProtected}
                  isCheckingPassword={isCheckingPassword}
                  isPasswordExposed={isPasswordExposed}
                  passwordStrength={passwordStrength}
                  onPasswordChange={setPassword}
                />
              )}
            </div>

            <div className="flex-1 space-y-4">
              <ExpirationSelector
                expiresInDays={expiresInDays}
                onChange={setExpiresInDays}
              />
              <ViewLimitInput
                viewLimit={viewLimit}
                onChange={setViewLimit}
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border p-6 space-y-6">
          <SubmitButton
            isSubmitting={isSubmitting}
            isCheckingPassword={isCheckingPassword}
            isDisabled={isSubmitDisabled}
          />

          <SecurityFeatures showAutopass={!isProtected} />
        </div>
      </form>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}